import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Input from '../ui/Input';
import Button from '../ui/Button';
import toast from 'react-hot-toast';

const { FiX, FiMail, FiUser, FiShield } = FiIcons;

const AddUserModal = ({ isOpen, onClose, onSuccess, tenantId, availableRoles = [] }) => {
    const [formData, setFormData] = useState({
        email: '',
        roleId: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                email: '',
                roleId: availableRoles.length > 0 ? availableRoles[0].id : ''
            });
            setErrors({});
        }
    }, [isOpen, availableRoles]);

    const validateForm = () => {
        const newErrors = {};

        // Email validation
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Role validation
        if (!formData.roleId) {
            newErrors.roleId = 'Please select a role';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            await onSuccess(formData);
            onClose();
            toast.success('Invitation sent successfully!');
        } catch (error) {
            console.error('Error sending invite:', error);
            toast.error(error.message || 'Failed to send invitation');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                    />

                    {/* Modal */}
                    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                        <SafeIcon icon={FiUser} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                            Invite User
                                        </h2>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Send an invitation to join your organization
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                >
                                    <SafeIcon icon={FiX} className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                                {/* Email Input */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SafeIcon icon={FiMail} className="w-5 h-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="user@example.com"
                                            className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border ${errors.email
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-200 dark:border-gray-600 focus:ring-blue-500'
                                                } rounded-lg focus:ring-2 focus:outline-none text-gray-900 dark:text-white transition-colors`}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        An invitation link will be sent to this email
                                    </p>
                                </div>

                                {/* Role Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Role <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SafeIcon icon={FiShield} className="w-5 h-5 text-gray-400" />
                                        </div>
                                        <select
                                            name="roleId"
                                            value={formData.roleId}
                                            onChange={handleChange}
                                            className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border ${errors.roleId
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-200 dark:border-gray-600 focus:ring-blue-500'
                                                } rounded-lg focus:ring-2 focus:outline-none text-gray-900 dark:text-white transition-colors appearance-none`}
                                            disabled={isSubmitting}
                                        >
                                            <option value="">Select a role</option>
                                            {availableRoles.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.roleId && (
                                        <p className="mt-1 text-sm text-red-500">{errors.roleId}</p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Select the role for the new user
                                    </p>
                                </div>

                                {/* Info Box */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                    <div className="flex items-start space-x-3">
                                        <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-blue-900 dark:text-blue-300">
                                            <p className="font-medium mb-1">How it works:</p>
                                            <ul className="list-disc list-inside space-y-1 text-xs">
                                                <li>User receives an email with an invitation link</li>
                                                <li>They can set their password and complete registration</li>
                                                <li>Invitation expires after 72 hours</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                <span>Sending...</span>
                                            </>
                                        ) : (
                                            <>
                                                <SafeIcon icon={FiMail} className="w-4 h-4" />
                                                <span>Send Invitation</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default AddUserModal;